/**
 * Register read service — fetches QMS register parent items via delegated Graph.
 *
 * Pure service layer — no React imports or state. Mirrors harmony-ap's
 * `complianceService` shape: Graph client + siteId + resolved listId in, typed
 * records out, with manual `@odata.nextLink` pagination (handled by the shared
 * `listItems` helper in `graphClient.ts`).
 *
 * Field names come ONLY from `config/registers.ts` (REGISTERS) — none are
 * hard-coded here. List ids are resolved upstream by `useGraphClient` and passed
 * in (never hard-coded).
 *
 * Key composition: the human reference is COMPOSED `<PREFIX>-<Id>` from the SP
 * item `Id` — see the composition site below (ISS-008).
 */

import type { Client } from '@microsoft/microsoft-graph-client';
import { listItems } from './graphClient';
import { REGISTERS } from '../config/registers';
import type { RegisterExtraFields } from '../config/registers';
import type {
  RegisterKey,
  RegisterRecord,
  AnyRegisterRecord,
  RegisterStatus,
  BusinessUnit,
  ClosureStatus,
  SeverityRating,
  ReportabilityDecision,
  RiskRatingLevel,
} from '../types/qms';

/**
 * Raw flattened SharePoint item (the `{ id, ...fields }` shape `listItems`
 * returns). `id` is the SP item id as a string; field values are unknown.
 */
export type RawItem = Record<string, unknown> & { id: string };

/**
 * Read a string-ish field, returning null when absent/empty. A `field` of `undefined` means the
 * register has no such column (its `extraFields` key is absent), so nothing is read at all.
 */
function str(item: RawItem, field: string | undefined): string | null {
  if (field === undefined) return null;
  const v = item[field];
  if (v === undefined || v === null || v === '') return null;
  return typeof v === 'string' ? v : String(v);
}

/**
 * Compose the human-facing reference from the SP item id.
 *
 * ISS-008: the SharePoint calculated `Reference` column does NOT resolve (every
 * row renders `<PREFIX>-0` because `[ID]` is not exposed to the calc engine).
 * The stable key is the immutable SP item `Id`, so we compose `<PREFIX>-<Id>`
 * client-side here and NEVER read the calc `Reference` column anywhere.
 */
function composeReference(prefix: string, id: number): string {
  return `${prefix}-${id}`;
}

/**
 * Map a raw SP item to the shared `RegisterRecord` spine using the register's
 * field map. Per-register extras are layered on by `getRecords`.
 */
function mapBase(
  item: RawItem,
  registerKey: RegisterKey,
): RegisterRecord {
  const cfg = REGISTERS[registerKey];
  const f = cfg.fields;
  // Per-register columns: an absent key means this list has no such column (ISS-223).
  const x: RegisterExtraFields = cfg.extraFields;
  const id = Number(item.id);

  return {
    id,
    // ISS-008: composed from the SP item id, NOT the calc Reference column.
    reference: composeReference(cfg.prefix, id),
    registerKey,
    title: str(item, f.title) ?? '',
    status: (str(item, f.status) as RegisterStatus | null) ?? 'New',
    businessUnit: str(item, f.businessUnit) as BusinessUnit | null,
    managingUnit: str(item, x.managingUnit) as BusinessUnit | null,
    closureStatus: (str(item, f.closureStatus) as ClosureStatus | null) ?? 'Open',
    created: str(item, f.created) ?? '',
    createdBy: readAuthor(item),
    severity: str(item, x.severity) as SeverityRating | null,
    description: str(item, f.description),
    // The register's own due-date column (Incidents ResolveBy, Complaints TargetResolutionOn,
    // CI CompleteBy, Risk ReviewDate; Feedback none → null).
    dueOn: str(item, x.dueOn),
    closedOn: str(item, x.closedOn),
  };
}

/**
 * The author's email (lower-cased) from the item envelope's `createdBy`, which `listItems`
 * carries through. It is what Own-only compares to the user's UPN, so it must be the email:
 * the fields' `Author` is a display name, or absent under a plain `fields` expand, and never
 * matched anyone (Kate's RSK-4, 26 Oct). Blank → null (fail closed). Incidents scope on
 * `ReportedByEmail` instead.
 */
function readAuthor(item: RawItem): string | null {
  const createdBy = item.createdBy as { user?: { email?: string } } | undefined;
  const email = createdBy?.user?.email;
  return typeof email === 'string' && email.trim() !== '' ? email.trim().toLowerCase() : null;
}

/**
 * Get all records for a register, typed to that register's shape.
 *
 * @param client - Graph client (delegated; from useGraphClient)
 * @param siteId - resolved Q&S site id (from useGraphClient)
 * @param listId - resolved parent list id (from useGraphClient.listIds — NOT
 *                 hard-coded). Empty string when the list is not yet provisioned.
 * @param registerKey - which register to read
 */
export async function getRecords(
  client: Client,
  siteId: string,
  listId: string,
  registerKey: RegisterKey,
): Promise<AnyRegisterRecord[]> {
  // Graceful empty-list handling: an unprovisioned list resolves to '' upstream;
  // the registers are empty today, so return [] rather than calling Graph.
  if (!listId) return [];

  const result = await listItems<RawItem>(client, siteId, listId, {
    orderby: 'fields/Created desc',
  });

  return result.items.map((item) => mapRegisterItem(item, registerKey));
}

/**
 * Map one raw (flattened) SharePoint item to its typed register record: the shared spine plus
 * the per-register extras. Field names come only from `REGISTERS[key]`. Exported for tests.
 */
export function mapRegisterItem(item: RawItem, registerKey: RegisterKey): AnyRegisterRecord {
  const base = mapBase(item, registerKey);
  const extra: RegisterExtraFields = REGISTERS[registerKey].extraFields;

  switch (registerKey) {
    case 'incidents':
      return {
        ...base,
        registerKey: 'incidents',
        reportabilityDecision: str(item, extra.reportabilityDecision) as ReportabilityDecision | null,
        reportedByEmail: str(item, extra.reportedByEmail),
        category: str(item, extra.category),
        occurredOn: str(item, extra.occurredOn),
        reportedOn: str(item, extra.reportedOn),
        reportedBy: str(item, extra.reportedBy),
        recipientName: str(item, extra.recipientName),
        recipientStream: str(item, extra.recipientStream),
      };
    case 'complaints':
      return {
        ...base,
        registerKey: 'complaints',
        category: str(item, extra.category),
      };
    case 'ci':
      return {
        ...base,
        registerKey: 'ci',
        category: str(item, extra.category),
        originatingItems: str(item, extra.originatingItems),
      };
    case 'feedback':
      return {
        ...base,
        registerKey: 'feedback',
        category: str(item, extra.category),
      };
    case 'risk':
      return {
        ...base,
        registerKey: 'risk',
        inherentRating: str(item, extra.inherentRating) as RiskRatingLevel | null,
        residualRating: str(item, extra.residualRating) as RiskRatingLevel | null,
        category: str(item, extra.category),
        originatingItems: str(item, extra.originatingItems),
      };
    default: {
      // Exhaustiveness guard — should be unreachable.
      const _never: never = registerKey;
      return _never;
    }
  }
}
